<template>
  <div class="calendar">
    <div class="calendar-header">
      <OButton pure class="u-center w-9 h-10 flex-shrink-0" :disabled="isPreviousMonthDisabled" @click="previousMonth">
        <OIcon icon="chevron-left-white" class="w-4 h-4" />
      </OButton>
      <div class="calendar-header-center">
        <div class="dropdown-wrapper">
          <div class="dropdown-toggle" @click="toggleMonthDropdown">
            {{ months[currentDate.month()] }}
            <OIcon icon="chevron-left-white" class="-rotate-90 w-3 h-3 ml-2" />
          </div>
          <div v-if="showMonthDropdown" class="dropdown-list">
            <div
              v-for="(month, idx) in months"
              :key="month"
              :class="['dropdown-item', { selected: idx === currentDate.month() }]"
              @click="selectMonth(idx)"
            >
              {{ month }}
            </div>
          </div>
        </div>
        <div class="dropdown-wrapper">
          <div class="dropdown-toggle" @click="toggleYearDropdown">
            {{ currentDate.year() }}
            <OIcon icon="chevron-left-white" class="-rotate-90 w-3 h-3 ml-2" />
          </div>
          <div v-if="showYearDropdown" class="dropdown-list">
            <div
              v-for="year in years"
              :key="year"
              :class="['dropdown-item', { selected: year === currentDate.year() }]"
              @click="selectYear(year)"
            >
              {{ year }}
            </div>
          </div>
        </div>
      </div>
      <OButton pure class="u-center w-9 h-10 flex-shrink-0" :disabled="isNextMonthDisabled" @click="nextMonth">
        <OIcon icon="chevron-right-white" class="w-4 h-4" />
      </OButton>
    </div>

    <div class="calendar-grid">
      <div v-for="day in weekDays" :key="day.toString()" class="week-day">{{ day }}</div>
      <div
        v-for="day in calendarDays"
        :key="day.date.toString()"
        :class="[
          'calendar-day',
          {
            'other-month': !day.isCurrentMonth,
            selected: isSelectedDate(day.date),
            today: isToday(day.date),
            disabled: isDateDisabled(day.date),
          },
        ]"
        @click="!isDateDisabled(day.date) && selectDate(day.date)"
      >
        {{ day.dayOfMonth }}
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref, onMounted, onBeforeUnmount } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { OButton, OIcon } from '~/modules/common/components/ui';
dayjs.locale('tr');

export default defineComponent({
  name: 'OCalendar',
  components: {
    OButton,
    OIcon,
  },
  props: {
    date: {
      type: Object,
      default: null,
    },
    minDate: {
      type: [String, Date],
      default: null,
    },
    maxDate: {
      type: [String, Date],
      default: null,
    },
  },
  setup(props, { emit }) {
    const selectedDate = ref(dayjs(props.date).isValid() ? dayjs(props.date) : dayjs());
    const currentDate = ref(dayjs(props.date).isValid() ? dayjs(props.date) : dayjs());
    const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

    const minDateValue = computed(() => (props.minDate ? dayjs(props.minDate) : null));
    const maxDateValue = computed(() => (props.maxDate ? dayjs(props.maxDate) : null));

    const currentMonthYear = computed(() => {
      return currentDate.value.format('MMMM YYYY');
    });

    const isPreviousMonthDisabled = computed(() => {
      if (!minDateValue.value) return false;
      const firstDayOfCurrentMonth = currentDate.value.startOf('month');
      return firstDayOfCurrentMonth.isBefore(minDateValue.value, 'month');
    });

    const isNextMonthDisabled = computed(() => {
      if (!maxDateValue.value) return false;
      const lastDayOfCurrentMonth = currentDate.value.endOf('month');
      return lastDayOfCurrentMonth.isAfter(maxDateValue.value, 'month');
    });

    const calendarDays = computed(() => {
      const start = currentDate.value.startOf('month');
      const end = currentDate.value.endOf('month');

      const startDay = start.day() || 7;
      const endDay = end.day() || 7;

      const startDate = start.subtract(startDay - 1, 'day');
      const endDate = end.add(7 - endDay, 'day');

      const days = [];
      let currentDay = startDate;

      while (currentDay.isBefore(endDate) || currentDay.isSame(endDate, 'day')) {
        days.push({
          date: currentDay,
          dayOfMonth: currentDay.date(),
          isCurrentMonth: currentDay.month() === currentDate.value.month(),
        });
        currentDay = currentDay.add(1, 'day');
      }

      return days;
    });

    const isDateDisabled = (date) => {
      if (minDateValue.value && date.isBefore(minDateValue.value, 'day')) {
        return true;
      }
      if (maxDateValue.value && date.isAfter(maxDateValue.value, 'day')) {
        return true;
      }
      return false;
    };

    const previousMonth = () => {
      if (!isPreviousMonthDisabled.value) {
        currentDate.value = currentDate.value.subtract(1, 'month');
      }
    };

    const nextMonth = () => {
      if (!isNextMonthDisabled.value) {
        currentDate.value = currentDate.value.add(1, 'month');
      }
    };

    const selectDate = (date) => {
      if (!isDateDisabled(date)) {
        selectedDate.value = date;
        emit('update:date', date.toDate());
        emit('select', date);
      }
    };

    const isSelectedDate = (date) => {
      return date.isSame(selectedDate.value, 'day');
    };

    const isToday = (date) => {
      return date.isSame(dayjs(), 'day');
    };

    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    const currentYear = dayjs().year();
    const years = Array.from({ length: currentYear - 1925 + 1 }, (_, i) => currentYear - i);

    // Dropdown states
    const showMonthDropdown = ref(false);
    const showYearDropdown = ref(false);

    const toggleMonthDropdown = () => {
      showMonthDropdown.value = !showMonthDropdown.value;
      showYearDropdown.value = false;
    };
    const toggleYearDropdown = () => {
      showYearDropdown.value = !showYearDropdown.value;
      showMonthDropdown.value = false;
    };
    const selectMonth = (monthIdx) => {
      currentDate.value = currentDate.value.month(monthIdx);
      showMonthDropdown.value = false;
    };
    const selectYear = (year) => {
      currentDate.value = currentDate.value.year(year);
      showYearDropdown.value = false;
    };

    // close dropdown when clicking outside
    const closeDropdown = (e) => {
      if (!e.target.closest('.calendar-header-center')) {
        showMonthDropdown.value = false;
        showYearDropdown.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener('click', closeDropdown);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('click', closeDropdown);
    });

    return {
      currentMonthYear,
      calendarDays,
      weekDays,
      previousMonth,
      nextMonth,
      selectDate,
      isSelectedDate,
      isToday,
      isDateDisabled,
      isPreviousMonthDisabled,
      isNextMonthDisabled,
      months,
      years,
      showMonthDropdown,
      showYearDropdown,
      toggleMonthDropdown,
      toggleYearDropdown,
      selectMonth,
      selectYear,
      currentDate,
    };
  },
});
</script>

<style scoped>
.calendar {
  width: 304px;
  margin: 0 auto;
  background-color: #000;
}

.calendar-header {
  display: flex;
  align-items: center;
  margin: 24px 0;
  width: 100%;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.week-day {
  text-align: center;
  font-weight: 600;
  padding: 8px;
  color: #666;
}

.calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  border-radius: 45px;
  overflow: hidden;
  transition: all 0.2s;
}

.calendar-day:hover:not(.disabled) {
  background-color: #6842ef;
}

.other-month {
  color: #424242;
}

.disabled {
  color: #424242;
  cursor: not-allowed;
}

.selected {
  background-color: #6842ef;
  color: white;
}

.selected:hover {
  background-color: #6842ef;
}

.today {
  font-weight: bold;
  border: 1px solid white;
}

.calendar-header-center {
  display: flex;
  gap: 12px;
  align-items: center;
}
.dropdown-wrapper {
  position: relative;
  width: 110px;
  height: 40px;
}
.dropdown-toggle {
  background: #19191a;
  color: #fff;
  border-radius: 48px;
  padding: 0.5rem 2.5rem;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
}
.dropdown-list {
  position: absolute;
  top: 110%;
  left: 0;
  right: 0;
  background: #181819;
  border-radius: 8px;
  border: 1px solid #424242;
  z-index: 10;
  max-height: 200px;
  width: 110px;
  overflow-y: auto;
}
.dropdown-item {
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #424242;
  text-align: center;
}
.dropdown-item:last-child {
  border-bottom: none;
}
.dropdown-item.selected,
.dropdown-item:hover {
  background: #6842ef;
}
</style>
